import { forwardRef, type SelectHTMLAttributes } from 'react';

type SelectSize = 'sm' | 'md';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helpText?: string;
  options: SelectOption[];
  placeholder?: string;
  selectSize?: SelectSize;
}

const sizeClasses: Record<SelectSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helpText, options, placeholder, selectSize = 'md', id, className = '', ...props }, ref) => {
    const selectId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className={className}>
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-surface-700 mb-1">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={[
            'block w-full rounded-lg border bg-white transition-colors appearance-none',
            'focus:outline-none focus:ring-2 focus:border-transparent',
            error
              ? 'border-danger-500 focus:ring-danger-500'
              : 'border-surface-300 focus:ring-primary-500',
            sizeClasses[selectSize],
            'disabled:bg-surface-50 disabled:text-surface-500 disabled:cursor-not-allowed',
          ].join(' ')}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-1 text-sm text-danger-600">{error}</p>}
        {helpText && !error && <p className="mt-1 text-sm text-surface-500">{helpText}</p>}
      </div>
    );
  },
);

Select.displayName = 'Select';
